/**********************************************************************
// @@@ START COPYRIGHT @@@
//        AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
//        Copyright 2018 - 2019
//        Avi Solar Energy Pvt Ltd.
//        Protected as an unpublished work.
//  The computer program listings, specifications and documentation
//  herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
//  and shall not be reproduced, copied, disclosed, or used in whole or in part for
//  any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <v-card>
    <Toolbar class="subheading"> {{ $t("MAP View") }} </Toolbar>
    <GmapMap
      ref="map"
      :center="center"
      :zoom="zoom"
      :options="mapStyles"
      :map-type-id="mapTypeId"
      class="map"
    >
      <GmapInfoWindow
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoWinOpen = false"
      >
        <div class="black--text" v-html="infoContent"></div>
      </GmapInfoWindow>
      <GmapMarker
        :key="i"
        v-for="(m, i) in markers"
        :position="m.position"
        :animation="m.alert && !hover ? 1 : 2"
        :duration="5000"
        :clickable="true"
        @mouseover="toggleInfoWindow(m, i)"
        @mouseout="infoWinOpen = false"
        @click="goToDashbord(m.plantId)"
      ></GmapMarker>
    </GmapMap>
  </v-card>
</template>

<script>
import Vue from "vue";
import * as VueGoogleMaps from "vue2-google-maps";
import { mapGetters, mapMutations } from "vuex";
import Toolbar from "@/components/common/Toolbar.vue";

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAP_KEY,
    libraries: "places",
  },
});

export default {
  props: ["theme"],
  components: {
    Toolbar,
  },
  data() {
    return {
      center: {
        lat: -12.270972,
        lng: 79.890688,
      },
      pName: "",
      zoom: 2,
      infoContent: "",
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      hover: false,
      // optional: offset infowindow so it visually sits nicely on top of our marker
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      mapTypeId: "terrain",
      markers: [],
    };
  },
  mounted() {
    const self = this;
    this.markers = this.getAllPlantCoordinates;
    if (this.markers.length === 1) {
      self.zoom = 4;
      if (
        self.markers[0].position.lat != null &&
        self.markers[0].position.lng != null &&
        !isNaN(self.markers[0].position.lat) &&
        !isNaN(self.markers[0].position.lng)
      ) {
        this.center.lng = parseFloat(self.markers[0].position.lat);
        this.center.lat = parseFloat(self.markers[0].position.lng);
      }
    } else {
      this.$refs["map"].$mapPromise.then((map) => {
        var bounds = new google.maps.LatLngBounds();
        self.markers.forEach((marker) => {
          if (
            marker.position.lat != null &&
            marker.position.lng != null &&
            !isNaN(marker.position.lat) &&
            !isNaN(marker.position.lng)
          ) {
            bounds.extend(
              new google.maps.LatLng(
                parseFloat(marker.position.lat),
                parseFloat(marker.position.lng)
              )
            );
          }
        });
        map.fitBounds(bounds);
        map.panToBounds(bounds);
      });
    }
  },
  methods: {
    ...mapMutations(["SET_SELECTED_PLANT_ID", "SET_SELECTED_COMPONENT_MENU"]),
    goToDashbord: function (pId) {
      this.SET_SELECTED_PLANT_ID(pId);
      this.SET_SELECTED_COMPONENT_MENU("plantOverview");
      let path = this.getRules(["rule", "access_config"])
        ? this.getRules(["rule", "access_config", "plantOverview", "path"])
        : "/plantOverview";
      this.$router.push(path);
    },
    toggleInfoWindow: function (marker, idx) {
      this.infoWindowPos = marker.position;
      this.infoContent = marker.plantName;
      if (this.currentMidx == idx) {
        this.infoWinOpen = !this.infoWinOpen;
      }
      // if different marker set infowindow to open and reset current marker index
      else {
        this.infoWinOpen = true;
        this.currentMidx = idx;
      }
    },
  },
  computed: {
    ...mapGetters(["getAllPlantCoordinates", "getRules", "getSelectedPlantId"]),
    mapStyles: function () {
      if (this.theme) {
        return {
          styles: [
            {
              elementType: "geometry",
              stylers: [
                {
                  color: "#242f3e",
                },
              ],
            },
            {
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#242f3e",
                },
              ],
            },
            {
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#746855",
                },
              ],
            },
            {
              featureType: "administrative.locality",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "geometry",
              stylers: [
                {
                  color: "#263c3f",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#6b9a76",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry",
              stylers: [
                {
                  color: "#38414e",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#212a37",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#9ca5b3",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [
                {
                  color: "#746855",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#1f2835",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#f3d19c",
                },
              ],
            },
            {
              featureType: "transit",
              elementType: "geometry",
              stylers: [
                {
                  color: "#2f3948",
                },
              ],
            },
            {
              featureType: "transit.station",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#d59563",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "geometry",
              stylers: [
                {
                  color: "#17263c",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#515c6d",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#17263c",
                },
              ],
            },
          ],
        };
      } else {
        return {
          styles: [
            {
              elementType: "geometry",
              stylers: [
                {
                  color: "#ebe3cd",
                },
              ],
            },
            {
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#523735",
                },
              ],
            },
            {
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#f5f1e6",
                },
              ],
            },
            {
              featureType: "administrative",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#c9b2a6",
                },
              ],
            },
            {
              featureType: "administrative.land_parcel",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#dcd2be",
                },
              ],
            },
            {
              featureType: "administrative.land_parcel",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#ae9e90",
                },
              ],
            },
            {
              featureType: "landscape.natural",
              elementType: "geometry",
              stylers: [
                {
                  color: "#dfd2ae",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "geometry",
              stylers: [
                {
                  color: "#dfd2ae",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#93817c",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "geometry.fill",
              stylers: [
                {
                  color: "#a5b076",
                },
              ],
            },
            {
              featureType: "poi.park",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#447530",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry",
              stylers: [
                {
                  color: "#f5f1e6",
                },
              ],
            },
            {
              featureType: "road.arterial",
              elementType: "geometry",
              stylers: [
                {
                  color: "#fdfcf8",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry",
              stylers: [
                {
                  color: "#f8c967",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#e9bc62",
                },
              ],
            },
            {
              featureType: "road.highway.controlled_access",
              elementType: "geometry",
              stylers: [
                {
                  color: "#e98d58",
                },
              ],
            },
            {
              featureType: "road.highway.controlled_access",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#db8555",
                },
              ],
            },
            {
              featureType: "road.local",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#806b63",
                },
              ],
            },
            {
              featureType: "transit.line",
              elementType: "geometry",
              stylers: [
                {
                  color: "#dfd2ae",
                },
              ],
            },
            {
              featureType: "transit.line",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#8f7d77",
                },
              ],
            },
            {
              featureType: "transit.line",
              elementType: "labels.text.stroke",
              stylers: [
                {
                  color: "#ebe3cd",
                },
              ],
            },
            {
              featureType: "transit.station",
              elementType: "geometry",
              stylers: [
                {
                  color: "#dfd2ae",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "geometry.fill",
              stylers: [
                {
                  color: "#b9d3c2",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "labels.text.fill",
              stylers: [
                {
                  color: "#92998d",
                },
              ],
            },
          ],
        };
      }
    },
  },
};
</script>
<style scoped>
/* 33% responsiveness */
@media only screen and (min-width: 2544px) and (max-width: 4238px) {
  .map {
    height: 680px;
  }
}
/* 33% responsiveness */
/* 50% responsiveness */
/* 67% responsiveness */
@media only screen and (min-width: 1904px) and (max-width: 2544px) {
  .map {
    height: 480px;
  }
}
/* 67% responsiveness */
/* 75% to 80% responsiveness */
@media only screen and (min-width: 1280px) and (max-width: 1904px) {
  .map {
    height: 428px;
  }
}
/* 75% to 80% responsiveness */
/* 90% responsiveness */
@media only screen and (min-width: 1394px) and (max-width: 1585px) {
  .map {
    height: 426px;
  }
}
/* 100% responsiveness */
</style>

